"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, FileCheck2, Clock3, ShieldCheck, Target, ArrowRight, CircleCheck as CheckCircle2, Users as Users2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const pillars = [{
  id: "cycle",
  title: "Navigating extended buying cycles",
  summary: "B2B and professional services deals rarely happen in a single visit.",
  description: "When a managing partner or CFO evaluates your firm, they research through several stages. I map search intent across the entire procurement timeline, from problem diagnosis to shortlist validation.",
  icon: Clock3,
  points: ["Targeting multi-stakeholder search queries", "Building comparison and methodology guides", "Addressing vendor evaluation checkpoints"]
}, {
  id: "authority",
  title: "Genuine subject-matter authority",
  summary: "Surface-level blog posts do not convince experienced decision-makers.",
  description: "Law firms, consultancies, and accounting practices depend on reputation. I translate your real internal expertise into comprehensive, indexable resources that establish credibility before the first introductory call.",
  icon: ShieldCheck,
  points: ["Executive perspectives and industry analyses", "Technical teardowns answering nuanced questions", "Clear differentiation against standard generalists"]
}, {
  id: "intent",
  title: "Qualified lead acquisition over vanity volume",
  summary: "A hundred relevant visitors beat ten thousand untargeted clicks.",
  description: "For a corporate law practice or boutique advisory, five qualified conversations a month can transform the firm. I focus your organic investment on high-intent terms used by buyers who have budget allocated.",
  icon: Target,
  points: ["Prioritising high-value transactional intent", "Optimising service landing pages for inquiries", "Eliminating wasteful low-intent traffic traps"]
}, {
  id: "reputation",
  title: "Protecting professional credibility",
  summary: "Your digital footprint directly affects your firm's brand valuation.",
  description: "Search engines inspect organizational signals, author credentials, and technical cleanliness. I maintain a clean structural architecture that positions your practice as an established benchmark in your sector.",
  icon: Compass,
  points: ["Rigorous technical foundations and page speed", "Entity and credential alignment across web assets", "Logical content hierarchies that ease navigation"]
}];
const targetSectors = ["Management & Strategy Consultancies", "Commercial & Corporate Law Firms", "Accountancy & Tax Advisory Practices", "B2B Software & SaaS Providers", "Specialist Creative & PR Agencies", "Engineering & Commercial Services"];
export default function Guidance() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = shouldReduceMotion ? {
    duration: 0.1
  } : {
    duration: 0.5,
    ease: "easeOut"
  };
  return <section id="b2bstrategicguidance" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute top-12 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 blur-3xl rounded-full opacity-40 -z-10" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-10 right-0 w-80 h-80 bg-accent/10 blur-2xl rounded-full opacity-50 -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={transitionConfig} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="border-primary/20 bg-secondary/50 text-primary font-medium px-3.5 py-1 text-xs uppercase tracking-wider">
              <Sparkles className="size-3.5 text-accent" />
              Direct Strategic Perspective
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: 0.1
        }} className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            How I approach SEO for high-value B2B and professional services
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: 0.2
        }} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When buying decisions take months and require boardroom consensus, vanity
            search volume won&apos;t help you. Here is how I shape search visibility
            so qualified decision-makers discover your expertise first.
          </motion.p>
        </div>

        {/* Strategic Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 24
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...transitionConfig,
            delay: shouldReduceMotion ? 0 : i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border/80 bg-card shadow-sm hover:shadow-md hover:border-primary/30 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
                  <div>
                    <CardHeader className="space-y-3 pb-2">
                      <div className="flex items-center justify-between">
                        <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-mono font-medium text-muted-foreground/80 tracking-wide">
                          0{i + 1}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-bold tracking-tight text-foreground pt-1">
                        {pillar.title}
                      </CardTitle>
                      <CardDescription className="text-primary font-medium text-sm">
                        {pillar.summary}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-2">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>
                      <div className="space-y-2 pt-2">
                        {pillar.points.map((point, ptIndex) => <div key={ptIndex} data-index={ptIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-medium">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Sectors & Consultation Card */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 20
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={transitionConfig} className="rounded-2xl border border-border bg-card p-8 sm:p-10 shadow-sm relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left side: Context and Personal Pledge */}
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
                <Users2 className="size-4" />
                <span>Tailored Practice Guidance</span>
              </div>
              <h3 className="font-default text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Strategic search guidance built for legal and professional practices
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Corporate law firms, advisory practices, and consultancies operate under distinct regulatory,
                confidentiality, and reputational standards. I design organic search strategies that reflect your
                real subject-matter authority and support long-term stakeholder evaluation. If your firm operates
                in any of these core sectors, I understand the commercial nuances you face:
              </p>

              {/* Sectors Pills */}
              <div className="flex flex-wrap gap-2 pt-2">
                {targetSectors.map((sector, sIndex) => <span key={sIndex} data-index={sIndex} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground text-xs font-medium border border-border/60">
                    <FileCheck2 className="size-3.5 text-primary" />
                    {sector}
                  </span>)}
              </div>
            </div>

            <Separator orientation="vertical" className="hidden lg:block h-36" />

            {/* Right side: Action Box */}
            <div className="lg:col-span-4 flex flex-col justify-center space-y-4 lg:pl-2">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Ready to assess your market positioning?
                </p>
                <p className="text-sm text-foreground/80">
                  Book a direct session with me to evaluate current organic visibility and discover where your firm is missing qualified corporate inquiries.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 pt-2">
                <Button asChild size="lg" className="w-full justify-center">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full justify-center">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}